import Payment from "src/app/pojos/payment";
import { ActivatedRoute, Router } from "@angular/router";
import { Component, EventEmitter, OnInit, Output } from "@angular/core";
import { MatSnackBar } from "@angular/material/snack-bar";
import { NgxSpinnerService } from "ngx-spinner";
import { PaymentApiService } from "src/app/api/payment-api.service";
import { RzpPaymentService } from "src/app/services/rzp-payment.service";
import { UtilService } from "src/app/services/util.service";
import { MatTabChangeEvent } from "@angular/material/tabs";
import { InvoiceDialogComponent } from "./invoice-dialog/invoice-dialog.component";
import { MatDialog } from "@angular/material/dialog";
import { Plan, GroupedPlan, groupByDuration } from "./plan";
import { PaymentStateService } from "src/app/services/payment-state.service";

declare var window: any;
@Component({
    selector: "app-plan",
    templateUrl: "./plan.component.html",
    styleUrls: ["./plan.component.scss"],
    standalone: false
})
export class PlanComponent implements OnInit {
  isGstFeatureEnabled: boolean = true;
  constructor(
    private snakbar: MatSnackBar,
    private paymentApi: PaymentApiService,
    private spinner: NgxSpinnerService,
    private rzpService: RzpPaymentService,
    private util: UtilService,
    private route: ActivatedRoute,
    private dialog: MatDialog,
    private router: Router,
    private paymentState: PaymentStateService
  ) {}
  plans: GroupedPlan[] = [];
  selectedPlan: Plan | null = null;
  defaultCountryCode: string = "india";
  selectedCountryCode: string = "india";
  isInternationalPayment: boolean = false;
  redirectUrl: string = null;
  subscriberDetail: any = {};

  selectedBranchId: string = null;

  ngOnInit() {
    this.redirectUrl = this.route.snapshot.queryParamMap.get("redirectUrl");
    let subId = this.route.snapshot.queryParamMap.get("subID");
    if (subId) {
      this.selectedBranchId = subId;
    }
  }

  ngAfterContentInit(): void {
    this.getSubscriberDetails();
  }

  async getSubscriberDetails() {
    try {
      let subscriber = await this.paymentApi.getSubscriberDetail();
      this.subscriberDetail = {
        subName: subscriber.data.subName,
        email: subscriber.data.email,
        name: subscriber.data.name,
      };

      if (subscriber?.data?.countryCode) {
        if (subscriber?.data?.countryCode.toLowerCase() !== "in") {
          this.isInternationalPayment = true;
          this.defaultCountryCode = "international";
        }
      }

      this.getPlan(this.defaultCountryCode);
    } catch (error) {}
  }

  getPlan(country: string) {
    this.spinner.show();
    this.paymentApi.getPaymentPlan(country).subscribe(
      (response: any) => {
        this.spinner.hide();
        this.plans = groupByDuration(response.data);
        this.selectedPlan = this.plans[0]?.plans?.[0];
      },
      (error) => {
        this.spinner.hide();
      }
    );
  }

  localizedCurrency(num) {
    if (this.util.isNull(num)) {
      return;
    }
    return this.util.localizedCurrency(num);
  }

  async payNow() {
    if (!this.selectedPlan) {
      this.snakbar.open("Please select a plan first", "Close", {
        duration: 3000,
      });
      return;
    }

    if (!this.selectedBranchId) {
      this.snakbar.open("Please select a gym branch first", "Close", {
        duration: 3000,
      });
      return;
    }

    let that = this;
    try {
      let payment: Payment =
        await this.rzpService.createPaymentRequestForGymManagementAtRzp(
          this.selectedPlan.planId,
          this.selectedBranchId,
          this.paymentState.billingInfo
        );

      let orderId = await this.rzpService.createRzpOrder(
        payment.finalAmount,
        payment.notes.paymentId,
        payment.currency
      );
      await this.rzpService.acceptPaymentFromRzp(payment, orderId);
      if (this.util.isNotNull(this.redirectUrl)) {
        window.open(this.redirectUrl, "_self");
      } else {
        window.open(location.origin + "/payment?s=s", "_self");
      }
    } catch (error) {
      that.spinner.hide();
      window.open(location.origin + "/payment?s=f", "_self");
    }
  }

  changeCountryEvent() {
    this.isInternationalPayment = this.selectedCountryCode === "international";
    this.getPlan(this.selectedCountryCode);
  }

  proceedToBilling(): void {
    if (!this.selectedPlan) {
      this.snakbar.open("Please select a plan first", "Close", {
        duration: 3000,
      });
      return;
    }

    if (!this.selectedBranchId) {
      this.snakbar.open("Please select a gym branch first", "Close", {
        duration: 3000,
      });
      return;
    }

    // Save plan data to state service
    this.paymentState.setPlanData(
      this.selectedPlan,
      this.selectedBranchId,
      this.isInternationalPayment
    );

    // Navigate to billing step
    this.router.navigate(["/billing"]);
  }

  // Keep the old method for backward compatibility
  openInvoiceDialog() {
    this.proceedToBilling();
  }

  onBranchSelected(branchId: string): void {
    this.selectedBranchId = branchId;
  }

  selectPlan(plan: Plan): void {
    this.selectedPlan = plan;
  }

  // Helper method to check if a plan is selected
  isPlanSelected(plan: Plan): boolean {
    return this.selectedPlan?._id === plan._id;
  }

  // Helper method to format currency with proper symbol
  formatCurrency(amount: number): string {
    const symbol = this.isInternationalPayment ? "$" : "₹";
    return `${symbol}${this.localizedCurrency(amount)}`;
  }

  hasDiscount(plan: Plan): boolean {
    return !!plan?.originalAmount && plan.originalAmount > plan.amount;
  }

  getSavingsAmount(plan: Plan): number {
    if (!this.hasDiscount(plan)) {
      return 0;
    }
    return plan.originalAmount - plan.amount;
  }

  // Helper method to check if a plan is the best value
  isBestValuePlan(plan: Plan): boolean {
    return plan.planType === "grow";
  }

  // Helper method to calculate GST amount
  calculateGstAmount(plan: Plan): number {
    if (!plan.gstApplicable || this.isInternationalPayment) {
      return 0;
    }
    return plan.amount * plan.gstPercentage;
  }

  // Helper method to format GST amount with currency
  formatGstAmount(plan: Plan): string {
    const gstAmount = this.calculateGstAmount(plan);
    if (gstAmount === 0) {
      return "";
    }
    const symbol = this.isInternationalPayment ? "$" : "₹";
    return `${symbol}${this.localizedCurrency(gstAmount)}`;
  }

  // Helper method to calculate total amount including GST
  calculateTotalAmountWithGst(plan: Plan): number {
    const gstAmount = this.calculateGstAmount(plan);
    return plan.amount + gstAmount;
  }

  // Helper method to format total amount with GST
  formatTotalAmountWithGst(plan: Plan): string {
    const totalAmount = this.calculateTotalAmountWithGst(plan);
    const symbol = this.isInternationalPayment ? "$" : "₹";
    return `${symbol}${this.localizedCurrency(totalAmount)}`;
  }
}
